/**
 * Which store the app is running on, decided once at start-up.
 *
 * There are two, and until now the app hardcoded the wrong one. `MemoryStore` keeps everything in
 * the browser it was typed into, which is fine for the offline preview and wrong for a league:
 * a manager who declares his slate on a laptop and opens the app on his phone finds an empty
 * form, and the commissioner reviewing slates sees only his own. `FirestoreStore` is the shared
 * one. This picks between them.
 *
 * THE ORDER MATTERS. Firestore is only reachable once Firebase Auth has a user, because the
 * security rules resolve identity from `links/{uid}` and a rule cannot ask an anonymous client
 * who it is. So the provider waits for the session before connecting, and renders nothing
 * store-dependent until it has an answer. The alternative — start on memory, swap later — means
 * the first thing a manager types goes into the wrong store and vanishes.
 *
 * FALLING BACK IS A FEATURE, NOT AN ERROR PATH. If Firestore cannot be reached the app drops to
 * the memory store and says so, loudly, on screen. That is a deliberate choice about one evening
 * of the year: on draft night, a board that works locally and needs its picks re-entered later
 * beats a blank page in somebody's basement. Every other night it is a warning worth acting on.
 */

import {
  createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { MemoryStore } from './memoryStore.js';
import { FirestoreStore } from './firestoreStore.js';
import type { LeagueStore } from './types.js';
import { useSession } from './session.js';
import { SEED_SEASON, SEED_SETTINGS_2026 } from './seed.js';
import { currentManagers } from '../domain/identity.js';
import { isStaleBuildError, markBuildHealthy, recoverFromStaleBuild } from './staleBuild.js';

export const SEASON = SEED_SEASON;

export interface StoreState {
  store: LeagueStore;
  /** True while we are still deciding. Nothing that writes should be on screen. */
  connecting: boolean;
  /**
   * Set when the app wanted Firestore and could not have it. Carries the reason, because
   * "offline" and "your account is not allowed to read the league" need different responses from
   * the person reading it.
   */
  fellBack: string | null;
}

const Ctx = createContext<StoreState | null>(null);

export function LeagueStoreProvider({ children }: { children: ReactNode }) {
  const { session, live, loading } = useSession();
  const uid = session?.uid;

  /**
   * Created once and never replaced. It is the offline store, the fallback, and the thing the
   * sandbox preview runs on; re-creating it on a re-render would throw away everything typed.
   */
  const memory = useRef<MemoryStore | null>(null);
  memory.current ??= new MemoryStore();

  const [state, setState] = useState<StoreState>(() => ({
    store: memory.current!,
    connecting: live,
    fellBack: null,
  }));

  useEffect(() => {
    // The offline single-file build: no Firebase, no network, nothing to wait for.
    if (!live) {
      setState({ store: memory.current!, connecting: false, fellBack: null });
      return;
    }
    // Still working out whether anybody is signed in.
    if (loading) return;
    // Signed out. The sign-in screen is next; it needs no store.
    if (!uid) {
      setState({ store: memory.current!, connecting: false, fellBack: null });
      return;
    }

    let cancelled = false;
    let opened: FirestoreStore | null = null;
    setState((s) => ({ ...s, connecting: true }));

    void (async () => {
      try {
        const { connectFirestore } = await import('./firestoreApi.js');
        const api = await connectFirestore();
        if (cancelled) return;
        opened = new FirestoreStore(api, SEASON);
        markBuildHealthy();
        setState({ store: opened, connecting: false, fellBack: null });
        void seedCalendar(opened);
        void seedRoles(opened);
      } catch (e) {
        if (cancelled) return;
        /*
         * A STALE BUILD IS NOT AN OUTAGE. The import above is the first lazy chunk most pages
         * ever ask for, so a page left open across a deploy fails right here, and the message
         * it fails with is about MIME types rather than networks. Reloading lands on the current
         * build. Only when that has already been tried once this session does it fall through
         * to the memory store — with a sentence that says what is actually wrong.
         */
        if (isStaleBuildError(e)) {
          if (recoverFromStaleBuild()) return;
          setState({
            store: memory.current!,
            connecting: false,
            fellBack: 'This page is from an older version of the app and could not load the '
              + 'current one. Close it fully and open the app again.',
          });
          return;
        }
        setState({
          store: memory.current!,
          connecting: false,
          fellBack: e instanceof Error ? e.message : 'Could not reach the league database.',
        });
      }
    })();

    return () => {
      cancelled = true;
      // Drop the listeners when the user signs out, or nine subscriptions keep running against a
      // uid the rules no longer accept and the console fills with permission errors.
      opened?.close();
    };
  }, [live, loading, uid]);

  const value = useMemo(() => state, [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Put the announced calendar into an empty league, once.
 *
 * An empty `seasons/2026/meta/settings` means the home screen has no deadlines on it and the
 * declaration form has nothing to lock against — the app looks broken on the first load after
 * deployment, and the fix is a commissioner typing five dates that are already written down in
 * the build. So the first commissioner or admin to open the app writes them.
 *
 * Three properties make this safe to run on every sign-in rather than as a migration:
 *
 *   It only fires when the document is ABSENT. Any subsequent edit is somebody's decision and is
 *   never overwritten — the seed loses to a real edit, always.
 *
 *   A manager's attempt fails harmlessly. The rules allow commissioners and admins to write the
 *   settings document, so nine of the ten get a permission error here, and it is caught. The
 *   tenth seeds it and everybody sees the result.
 *
 *   It is idempotent under a race. Two commissioners opening the app at once both write the same
 *   document with the same contents.
 */
async function seedCalendar(store: FirestoreStore) {
  try {
    /*
     * ASKED OF THE DATABASE, NOT OF THE CACHE. This was `if (await store.settings(SEASON)) return`,
     * and `settings()` answers from the live snapshot — null while the subscription is still in
     * flight, which is indistinguishable from null because the league is new. On a slow first
     * snapshot the seed fired and wrote defaults over real, saved deadlines.
     */
    if (await store.hasSettings(SEASON)) return;
    await store.saveSettings(SEED_SETTINGS_2026);
  } catch {
    // Not a commissioner, or offline. Either way somebody else will do it, and the app works
    // without it — every deadline simply reads as "not set" until then.
  }
}

/**
 * Put the roles into `league/config`, once.
 *
 * The security rules cannot read `gm-roster.json` — rules see the database and nothing else — so
 * until this document exists they fall back to a hardcoded `['TIM']` and an empty commissioner
 * list. That bootstrap is deliberately minimal: it exists to make the very first sign-in work,
 * not to run the league. Without this, Rajan and Vishal would be able to read everything and
 * write nothing, which is a confusing way to discover you are a commissioner.
 *
 * Seeded from the roster in the build, which is where roles are already decided and reviewed.
 * After this the DOCUMENT is the authority: change somebody's role in the app and the rules
 * follow, with no redeploy.
 */
async function seedRoles(store: FirestoreStore) {
  try {
    // Imported statically: identity.ts is in the main chunk anyway — thirty modules import it —
    // so a dynamic import here buys no code splitting and only makes the build warn about it.
    const roster = currentManagers();
    const of = (role: string) => roster.filter((m) => m.role === role).map((m) => m.id);
    const adminGms = of('admin');
    if (adminGms.length === 0) return; // Never write a config that locks everybody out.
    await store.putConfigIfMissing({ adminGms, commissionerGms: of('commissioner') });
  } catch {
    // Only an admin may write it, so this is the expected outcome for nine of the ten.
  }
}

export function useLeagueStore(): StoreState {
  const c = useContext(Ctx);
  if (!c) throw new Error('useLeagueStore must be used inside <LeagueStoreProvider>');
  return c;
}

/** The memory store behind either front end, when there is one. Used by the backup screen. */
export const asMemoryStore = (s: LeagueStore): MemoryStore | null =>
  (s.kind === 'memory' ? (s as MemoryStore) : null);

export const asFirestore = (s: LeagueStore): FirestoreStore | null =>
  (s.kind === 'firestore' ? (s as FirestoreStore) : null);
